import React from 'react';
import { Car, Eye, Pencil, Phone, Trash2, Unlink } from 'lucide-react';
import type { Driver } from '../../../models/DriverModel';
import DriverPassSummaryBadges from './DriverPassSummaryBadges';
import './DriverCard.css';

export type DriverCardProps = {
  driver: Driver;
  onEditPage?: (driver: Driver) => void;
  onEditForm?: (driver: Driver) => void;
  onDelete?: (id: number) => void;
  onReleaseVehicle?: (driver: Driver) => void;
  releaseVehicleLoading?: boolean;
  showActions?: boolean;
};

function getInitials(first?: string, last?: string): string {
  const a = (first ?? '').trim().charAt(0);
  const b = (last ?? '').trim().charAt(0);
  const initials = `${a}${b}`.toUpperCase();
  return initials || '?';
}

export function formatNextService(driver: Driver): string {
  const next = driver.next_service;
  if (next?.label) return next.label;

  if (next?.company_name) {
    const time = next.time ? ` ${next.time}` : '';
    const pax =
      next.passengers_count != null && next.passengers_count >= 0
        ? ` · ${next.passengers_count} pax`
        : '';
    return `Servicio asignado a ${next.company_name}${time}${pax}`;
  }

  if (driver.status === 'En servicio') {
    return 'Servicio asignado';
  }

  return 'Sin servicio asignado';
}

export function driverHasUpcomingService(driver: Driver): boolean {
  return (
    Boolean(driver.next_service?.company_name || driver.next_service?.label) ||
    driver.status === 'En servicio'
  );
}

/**
 * Card de conductor (estilo 21st.dev / profile card).
 * Recibe datos por props (DIP); no conoce la página ni el servicio.
 */
const DriverCard: React.FC<DriverCardProps> = ({
  driver,
  onEditPage,
  onEditForm,
  onDelete,
  onReleaseVehicle,
  releaseVehicleLoading = false,
  showActions = true,
}) => {
  const fullName = `${driver.first_name ?? ''} ${driver.last_name ?? ''}`.trim() || 'Sin nombre';
  const vehicleLabel = driver.vehicle_license_plate?.trim() || null;
  const nextServiceText = formatNextService(driver);
  const hasService = driverHasUpcomingService(driver);
  const canReleaseVehicle = Boolean(vehicleLabel && onReleaseVehicle && driver.id != null);

  return (
    <article className="driver-card" aria-label={`Conductor ${fullName}`}>
      <header className="driver-card__header">
        <div className="driver-card__avatar" aria-hidden title={fullName}>
          <span className="driver-card__avatar-fallback">
            {getInitials(driver.first_name, driver.last_name)}
          </span>
        </div>

        <div className="driver-card__identity">
          <h3 className="driver-card__name">{driver.first_name || '—'}</h3>
          <p className="driver-card__lastname">{driver.last_name || '—'}</p>
          <p className="driver-card__address" title={driver.address || undefined}>
            {driver.address?.trim() || 'Sin dirección'}
          </p>
          <p className="driver-card__phone">
            <Phone size={14} strokeWidth={2} aria-hidden />
            <span>{driver.phone?.trim() || 'Sin celular'}</span>
          </p>
        </div>

        {driver.status ? (
          <span
            className={`driver-card__status${
              driver.status === 'Operativo' ? ' driver-card__status--ok' : ''
            }`}
          >
            {driver.status}
          </span>
        ) : null}
      </header>

      <section
        className="driver-card__section"
        aria-labelledby={`driver-passes-${driver.id ?? 'new'}`}
      >
        <div className="driver-card__section-grid">
          <div>
            <h4
              id={`driver-passes-${driver.id ?? 'new'}`}
              className="driver-card__section-title"
            >
              Pases habilitados
            </h4>
            <DriverPassSummaryBadges summary={driver.pass_summary} />
          </div>
          <div>
            <h4 className="driver-card__section-title">Vehículo asignado</h4>
            <div className="driver-card__vehicle">
              <span className="driver-card__vehicle-icon" aria-hidden>
                <Car size={15} strokeWidth={2} />
              </span>
              <span
                className={
                  vehicleLabel ? 'driver-card__vehicle-plate' : 'driver-card__vehicle-empty'
                }
              >
                {vehicleLabel || 'Sin vehículo'}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section
        className="driver-card__section driver-card__section--service"
        aria-label="Próximo servicio"
      >
        <h4 className="driver-card__section-title">Prox. servicio</h4>
        <p
          className={`driver-card__service-text${
            hasService ? ' driver-card__service-text--assigned' : ''
          }`}
        >
          {nextServiceText}
        </p>
        {driver.shift_name ? (
          <p className="driver-card__shift">Turno: {driver.shift_name}</p>
        ) : null}
      </section>

      {showActions && (onEditPage || onEditForm || onDelete || canReleaseVehicle) ? (
        <footer className="driver-card__actions">
          {canReleaseVehicle ? (
            <button
              type="button"
              className="data-table__action data-table__action--release"
              onClick={() => onReleaseVehicle?.(driver)}
              title="Desasignar vehículo"
              aria-label={`Desasignar vehículo de ${fullName}`}
              disabled={releaseVehicleLoading}
            >
              <Unlink size={16} strokeWidth={2} aria-hidden />
              <span>{releaseVehicleLoading ? 'Desasignando…' : 'Desasignar vehículo'}</span>
            </button>
          ) : null}
          {onEditForm ? (
            <button
              type="button"
              className="data-table__action data-table__action--edit"
              onClick={() => onEditForm(driver)}
              title="Editar conductor"
              aria-label={`Editar ${fullName}`}
            >
              <Pencil size={16} strokeWidth={2} aria-hidden />
              <span>Editar</span>
            </button>
          ) : null}
          {onEditPage ? (
            <button
              type="button"
              className="data-table__action data-table__action--detail"
              onClick={() => onEditPage(driver)}
              title="Ver detalle del conductor"
              aria-label={`Ver detalle de ${fullName}`}
            >
              <Eye size={16} strokeWidth={2} aria-hidden />
              <span>Ver detalle</span>
            </button>
          ) : null}
          {onDelete && driver.id != null ? (
            <button
              type="button"
              className="data-table__action data-table__action--danger"
              onClick={() => onDelete(Number(driver.id))}
              title="Eliminar conductor"
              aria-label={`Eliminar ${fullName}`}
            >
              <Trash2 size={16} strokeWidth={2} aria-hidden />
              <span>Eliminar</span>
            </button>
          ) : null}
        </footer>
      ) : null}
    </article>
  );
};

export default DriverCard;
